import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { auth } from '../../firebase';
import '../../css/CommandCenterFilePickerModal.css';

/**
 * CommandCenterFilePickerModal
 *
 * Lists every file the user owns across the Bluebird Command Center (loose
 * library files plus attachments from all apps) and returns the chosen one
 * as a File object, so it can flow through the existing attachment upload
 * pipeline unchanged (copy-on-attach).
 *
 * Props:
 *   isOpen             - boolean, controls visibility
 *   onClose            - called when the modal is dismissed
 *   onSelect           - called with a File object when a file is picked
 *   allowedExtensions  - e.g. ['.zip', '.pdf'] — other files are shown disabled
 *   maxSizeBytes       - files above this are shown disabled
 */

const API_BASE = 'https://bluebirddocumentationadmin.pythonanywhere.com';

const SOURCE_LABELS = {
  command_center: 'Command Center library',
  documentation: 'Documentation',
  team_workspaces: 'Team Workspaces',
  task_management: 'Task Management',
  audio_transcription: 'Audio Transcription',
  document_markup: 'Document Markup',
};

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const getHeaders = async () => {
  const user = auth.currentUser;
  const token = user ? await user.getIdToken() : null;
  return {
    'Authorization': token,
    'Auth-Token': `Bearer ${token}`,
    'Requesting-User-Uid': user ? user.uid : '',
  };
};

const CommandCenterFilePickerModal = ({ isOpen, onClose, onSelect, allowedExtensions, maxSizeBytes }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fetchingId, setFetchingId] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const headers = await getHeaders();
        const response = await fetch(`${API_BASE}/command-center/files/`, { headers });
        if (!response.ok) throw new Error(`Failed to load your Command Center files (${response.status})`);
        const body = await response.json();
        if (!cancelled) setFiles(body.files || []);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) setSearch('');
  }, [isOpen]);

  if (!isOpen) return null;

  const visibleFiles = search
    ? files.filter((f) => (f.name || '').toLowerCase().includes(search.toLowerCase()))
    : files;

  const blockedReason = (item) => {
    if (!item.download_path) {
      return 'No download available for this file type';
    }
    const extension = `.${(item.name || '').split('.').pop().toLowerCase()}`;
    if (allowedExtensions && !allowedExtensions.includes(extension)) {
      return 'File type not allowed here';
    }
    if (maxSizeBytes && item.size_bytes > maxSizeBytes) {
      return `Larger than the ${Math.round(maxSizeBytes / (1024 * 1024))}MB limit`;
    }
    return null;
  };

  const handlePick = async (item) => {
    setFetchingId(`${item.source}:${item.id}`);
    setError('');
    try {
      const headers = await getHeaders();
      const response = await fetch(`${API_BASE}${item.download_path}`, { headers });
      if (!response.ok) throw new Error(`Failed to fetch file (${response.status})`);
      const blob = await response.blob();
      const file = new File([blob], item.name, { type: item.file_type || blob.type });
      onSelect(file);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setFetchingId(null);
    }
  };

  return ReactDOM.createPortal(
    <div className="CCPicker__overlay" onClick={onClose}>
      <div className="CCPicker__modal" onClick={(e) => e.stopPropagation()}>
        <div className="CCPicker__header">
          <h3 className="CCPicker__title">Choose from Command Center</h3>
          <button type="button" className="CCPicker__close" onClick={onClose} title="Close">×</button>
        </div>
        <div className="CCPicker__body">
          {error && <div className="CCPicker__error">{error}</div>}
          {!loading && files.length > 0 && (
            <input
              type="search"
              className="CCPicker__search"
              placeholder="Search files…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          )}
          {loading ? (
            <div className="CCPicker__status">Loading your library…</div>
          ) : files.length === 0 && !error ? (
            <div className="CCPicker__status">
              No files found in your Command Center. Upload files at{' '}
              <a href="https://cc.bluebird-documentation.com/files" target="_blank" rel="noreferrer">
                cc.bluebird-documentation.com
              </a>.
            </div>
          ) : visibleFiles.length === 0 ? (
            <div className="CCPicker__status">No files match “{search}”.</div>
          ) : (
            <ul className="CCPicker__list">
              {visibleFiles.map((item) => {
                const blocked = blockedReason(item);
                return (
                  <li key={`${item.source}:${item.id}`} className="CCPicker__item">
                    <div className="CCPicker__item-info">
                      <span className="CCPicker__item-name">{item.name}</span>
                      <span className="CCPicker__item-meta">
                        {SOURCE_LABELS[item.source] || item.source} · {formatBytes(item.size_bytes)}
                        {blocked ? ` · ${blocked}` : ''}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="CCPicker__select"
                      disabled={!!blocked || fetchingId === `${item.source}:${item.id}`}
                      onClick={() => handlePick(item)}
                    >
                      {fetchingId === `${item.source}:${item.id}` ? 'Fetching…' : 'Select'}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CommandCenterFilePickerModal;
